/**
 * The LLM models this organisation's plan exposes —
 * `GET /organizations/{id}/llm_models`. Ported from `bimgo-ai-web`'s `useModel`.
 *
 * **This one is api-v2, not the agent.** `/ask` and `/abort` go to the local
 * agent; the model catalogue is the org's plan and lives on the server, and the
 * web client routes it to `BASE_URL` regardless of client mode. Sending it to
 * the agent would 404.
 *
 * Picking a model matters here in a way it does not on the web: the agent
 * validates the choice against the same plan before it bills, and answers **403
 * FORBIDDEN** for a model the org cannot use (`routes/ask.ts`, `routes/skills.ts`
 * — this said 400 for a long time and nothing on this side branched on it). So the
 * picker must offer exactly this list and nothing else — and when the list cannot
 * be fetched the picker is hidden rather than guessed at, leaving the agent to use
 * its own default, which the agent checks too but only warns about.
 *
 * `context_window` is carried through because it is the **denominator for the
 * context meter**. The `usage` event reports one too; this is the one available
 * before the first turn completes.
 */
import { OrganizationsError, readDetail } from "./organizations";
import { bearer } from "./accessToken";
import { apiFetch } from "./apiFetch";

export interface ModelOption {
  provider: string;
  model: string;
  /** `display_name` — what the picker shows. */
  title: string;
  isDefault: boolean;
  /** Null when the plan's catalogue publishes none. */
  contextWindow: number | null;
}

export interface CurrentModel {
  provider: string;
  model: string;
}

/** Display order within a provider: Thinking → Coding → Instant. */
const TYPE_ORDER: Record<string, number> = { thinking: 0, coding: 1, instant: 2 };

interface ApiModelRow {
  model: string;
  display_name: string;
  provider: string;
  type: string;
  context_window: number | null;
  is_default: boolean;
}

export async function getOrgModels(
  token: string | null,
  orgId: string,
  deps: { apiBaseUrl: string; fetchImpl?: typeof fetch },
): Promise<ModelOption[]> {
  if (!token) throw new OrganizationsError({ kind: "unauthenticated" }, "not signed in");
  const fetchImpl = deps.fetchImpl ?? apiFetch;
  const url = `${deps.apiBaseUrl.replace(/\/+$/, "")}/organizations/${encodeURIComponent(
    orgId,
  )}/llm_models`;

  let response: Response;
  try {
    response = await fetchImpl(url, { headers: { Authorization: bearer(token) } });
  } catch (error) {
    throw new OrganizationsError({ kind: "network", detail: String(error) }, String(error));
  }

  if (response.status === 401 || response.status === 403) {
    throw new OrganizationsError({ kind: "unauthenticated" }, "session rejected by the API");
  }

  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const detail = readDetail(body);
    throw new OrganizationsError(
      { kind: "http", status: response.status, detail },
      detail ?? `HTTP ${response.status}`,
    );
  }

  const rows = (body as { data?: unknown } | null)?.data;
  if (!Array.isArray(rows)) return [];
  return sortModels(rows as ApiModelRow[]).map((m) => ({
    provider: m.provider,
    model: m.model,
    title: m.display_name,
    isDefault: m.is_default === true,
    contextWindow: typeof m.context_window === "number" ? m.context_window : null,
  }));
}

/** Provider, then UX tier, then name — the web client's order exactly. */
function sortModels(rows: readonly ApiModelRow[]): ApiModelRow[] {
  return [...rows].sort((a, b) => {
    if (a.provider !== b.provider) return String(a.provider).localeCompare(String(b.provider));
    const ra = TYPE_ORDER[a.type] ?? 99;
    const rb = TYPE_ORDER[b.type] ?? 99;
    if (ra !== rb) return ra - rb;
    return String(a.display_name).localeCompare(String(b.display_name));
  });
}

/**
 * Which model to start on: the plan's default, else the first offered.
 *
 * Keeps the user's current pick when it is still in the list — a refetch (a new
 * token, a plan change) must not silently move them off the model they chose.
 */
export function resolveModel(
  models: readonly ModelOption[],
  current: CurrentModel | null,
): CurrentModel | null {
  if (current && models.some((m) => m.provider === current.provider && m.model === current.model)) {
    return current;
  }
  const preferred = models.find((m) => m.isDefault) ?? models[0];
  return preferred ? { provider: preferred.provider, model: preferred.model } : null;
}

/** The selected model's window, for the meter before the first `usage` event. */
export function activeContextWindow(
  models: readonly ModelOption[],
  current: CurrentModel | null,
): number | null {
  if (!current) return null;
  const selected = models.find(
    (m) => m.provider === current.provider && m.model === current.model,
  );
  return selected?.contextWindow ?? null;
}

/** What the agent accepts for `agent_mode`. `hitl` is its own default. */
export const AGENT_MODES = ["hitl", "auto", "plan"] as const;
export type AgentMode = (typeof AGENT_MODES)[number];

/**
 * What the agent accepts for `reasoning_effort` (#111), in the order the chip
 * lists them. `medium` is the agent's own default — what every turn ran at
 * before the chip existed — so it is also what the store falls back to.
 */
export const REASONING_EFFORTS = ["none", "low", "medium", "high", "xhigh", "max"] as const;
export type ReasoningEffort = (typeof REASONING_EFFORTS)[number];
export const DEFAULT_REASONING_EFFORT: ReasoningEffort = "medium";

export function isReasoningEffort(value: unknown): value is ReasoningEffort {
  return typeof value === "string" && (REASONING_EFFORTS as readonly string[]).includes(value);
}

/**
 * Whether the reasoning chip applies to the selected model.
 *
 * **The plan catalogue does not say.** Its `type` column is a UX/pricing tier
 * (`instant` | `thinking` | `coding`), not a capability: `gpt-5-mini` is seeded
 * as `instant` and still takes `reasoning.effort`, so reading the tier would
 * hide the chip on exactly the models a user turns effort *down* for. Until
 * api-v2 publishes a real flag (#111, "Catalogue") this is the desktop's own
 * rule by name, matching what the agent can actually pass through: OpenAI's
 * reasoning line and o-series take `reasoning.effort`; every Claude takes
 * `thinking` + `output_config.effort`. Anything else — a provider the agent
 * does not build, an unknown name — says no, which only hides a chip.
 *
 * **The OpenAI half is a generation floor, not a list of families** (owner): it was
 * `/^gpt-5/`, so selecting gpt-6 silently hid the chip and the turn ran at the agent's default
 * effort with no way to change it. `reasoning.effort` has been part of the line since 5 and
 * nothing suggests a later generation drops it, so 5 *and later* qualifies and a new generation
 * needs no edit here. The agent's own mapping already copes with an unrecognised name: an
 * effort it cannot confirm the model accepts is clamped to `high` rather than sent and refused.
 */
export function supportsReasoningEffort(current: CurrentModel | null): boolean {
  if (!current) return false;
  const name = current.model.toLowerCase();
  if (current.provider === "openai") {
    if (/^o[1-9]/.test(name)) return true;
    const generation = /^gpt-(\d+)/.exec(name);
    return generation !== null && Number(generation[1]) >= 5;
  }
  if (current.provider === "anthropic") return name.startsWith("claude");
  return false;
}
